import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft, ArrowsClockwise, ShieldWarning, CheckCircle,
  WarningCircle, ArrowSquareOut,
} from "@phosphor-icons/react";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AdminFunderStatus = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [reactivating, setReactivating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/funder-status`);
      setReport(res.data?.report || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/admin/funder-status/run-now`);
      toast.success("Sweep started — results in ~2 minutes. Refresh shortly.");
      setTimeout(load, 120000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start sweep");
    } finally {
      setRunning(false);
    }
  };

  const reactivate = async (grantId) => {
    setReactivating(grantId);
    try {
      await axios.post(`${API}/admin/grants/${grantId}/reactivate`);
      toast.success("Grant re-activated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to re-activate");
    } finally {
      setReactivating(null);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="admin-funder-status-page">
      <header className="border-b border-zinc-800/60 bg-zinc-950/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-zinc-400 hover:text-emerald-400 text-sm" data-testid="back-to-admin-link">
            <ArrowLeft className="w-4 h-4" />
            Back to admin
          </Link>
          <Button
            onClick={runNow}
            disabled={running}
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-400 text-black"
            data-testid="run-sweep-btn"
          >
            <ArrowsClockwise className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} />
            {running ? "Sweep running…" : "Run sweep now"}
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full mb-3 text-[10px] font-bold uppercase tracking-[0.18em] bg-amber-500/15 border border-amber-500/40 text-amber-300">
            <ShieldWarning weight="fill" className="w-3 h-3" />
            Funder Status Sweep
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">Auto-deactivation report</h1>
          <p className="text-sm text-zinc-400 max-w-2xl">
            Weekly background sweep that fetches every active grant's funder website and scans for closure language ("suspended", "not accepting", "temporarily closed"). Auto-deactivates matches so users never see grants that are paused.
          </p>
        </div>

        {loading && (
          <p className="text-sm text-zinc-500" data-testid="funder-status-loading">Loading latest report…</p>
        )}

        {!loading && !report && (
          <Card className="bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-6 text-center" data-testid="no-report-empty-state">
              <p className="text-zinc-400 text-sm">No sweep has run yet. Hit "Run sweep now" to kick one off.</p>
            </CardContent>
          </Card>
        )}

        {!loading && report && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="funder-status-summary">
              <SummaryStat label="Scanned" value={report.total_grants_scanned} testId="stat-scanned" />
              <SummaryStat label="Checked OK" value={report.total_grants_checked} testId="stat-checked" />
              <SummaryStat
                label="Auto-deactivated"
                value={report.deactivated_count}
                accent={report.deactivated_count > 0 ? "rose" : "emerald"}
                testId="stat-deactivated"
              />
              <SummaryStat label="Skipped (403/error)" value={report.skipped_count + report.errors_count} testId="stat-skipped" />
            </div>

            <p className="text-xs text-zinc-500 mb-6">
              Last sweep finished {formatDate(report.finished_at)} · Report ID <span className="font-mono">{report.report_id}</span>
            </p>

            {/* Deactivated section */}
            <section className="mb-12" data-testid="deactivated-section">
              <h2 className="text-base font-bold tracking-tight mb-4 flex items-center gap-2">
                <WarningCircle weight="fill" className="w-4 h-4 text-rose-400" />
                Auto-deactivated ({report.deactivated_count})
              </h2>
              {report.deactivated_count === 0 ? (
                <Card className="bg-emerald-500/5 border-emerald-500/30">
                  <CardContent className="p-5 flex items-start gap-3" data-testid="no-deactivations-empty">
                    <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-300">All clean.</p>
                      <p className="text-xs text-zinc-400 mt-1">No grants matched closure language on this sweep. Every active grant's funder website looks open.</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {(report.deactivated || []).map((d) => (
                    <Card key={d.grant_id} className="bg-zinc-950/60 border-rose-500/30">
                      <CardContent className="p-5">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-white">{d.title}</p>
                            <p className="text-xs text-zinc-500 mt-0.5">{d.funder_name}</p>
                            <p className="text-xs text-zinc-400 mt-3 italic leading-snug" data-testid="deactivated-snippet">
                              "...{(d.matched_phrase || "").slice(0, 220)}..."
                            </p>
                            {d.funder_website && (
                              <a
                                href={d.funder_website}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 mt-3 font-mono"
                              >
                                {d.funder_website}
                                <ArrowSquareOut className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          <Button
                            onClick={() => reactivate(d.grant_id)}
                            disabled={reactivating === d.grant_id}
                            size="sm"
                            variant="outline"
                            className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 shrink-0"
                            data-testid={`reactivate-${d.grant_id}`}
                          >
                            {reactivating === d.grant_id ? "Re-activating…" : "Re-activate"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>

            {/* Skipped section (collapsed by default — just count) */}
            {(report.skipped_count > 0 || report.errors_count > 0) && (
              <section data-testid="skipped-section">
                <h2 className="text-base font-bold tracking-tight mb-4 flex items-center gap-2 text-zinc-400">
                  Skipped & errors ({report.skipped_count + report.errors_count})
                </h2>
                <p className="text-xs text-zinc-500 mb-3 max-w-2xl">
                  These funder URLs returned HTTP 403 (anti-bot blocks) or failed to fetch.
                  We deliberately do NOT auto-deactivate these — they're almost always real funders
                  (Duke Energy, SCAC, USDA, SCRA, etc.) whose sites block our scraper.
                </p>
                <Card className="bg-zinc-950/40 border-zinc-800">
                  <CardContent className="p-4">
                    <details className="text-xs text-zinc-400">
                      <summary className="cursor-pointer hover:text-zinc-200">Show details</summary>
                      <ul className="mt-3 space-y-2">
                        {(report.skipped || []).map((s) => (
                          <li key={s.grant_id} className="text-xs">
                            <span className="text-zinc-300">{s.title}</span>
                            <span className="text-zinc-500"> — {s.reason}</span>
                          </li>
                        ))}
                        {(report.errors || []).map((s) => (
                          <li key={s.grant_id} className="text-xs">
                            <span className="text-zinc-300">{s.title}</span>
                            <span className="text-rose-400"> — {s.error}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </CardContent>
                </Card>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, accent = "zinc", testId }) => {
  const colorMap = {
    zinc: "text-zinc-200 border-zinc-800",
    emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
    rose: "text-rose-300 border-rose-500/40 bg-rose-500/5",
  };
  return (
    <div
      className={`rounded-sm border px-4 py-3 bg-zinc-950/40 ${colorMap[accent] || colorMap.zinc}`}
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</p>
      <p className="text-2xl font-black tracking-tight">{value ?? "—"}</p>
    </div>
  );
};

export default AdminFunderStatus;
